import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  afterNextRender,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ModalComponent } from '../../../ui/modal-component/modal-component.component';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { CustomizableButtonComponent } from '../../../ui/customizable-button/customizable-button.component';
import { StatusComponent } from '../../../ui/status/status.component';
import { SmsBubbleComponent } from '../../../messages/sms-bubble/sms-bubble.component';
import { BroadcastMessageService } from '../../../../services/broadcast/broadcast.service';
import { ISenderID } from '../../../../services/broadcast/types/broadcastTypes';
import { ITestContact } from '../../../../types/settingsDataModule';
import { displayPhone } from '../../../../pages/organization/settings/settings-roles';
import { smsParts } from '../../../../../utils/sms';

type Step = 'compose' | 'confirm' | 'result';

interface SendResult {
  statusText: string;
  status: string;
  detail: string;
  reference: string;
}

/**
 * Send one real SMS to a test number: write it, pick the sender ID, confirm,
 * then see what the gateway said. Uses the same endpoint as the composer's
 * "Send test".
 */
@Component({
  selector: 'app-send-test-sms-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ModalComponent,
    InputFieldComponent,
    CustomizableButtonComponent,
    StatusComponent,
    SmsBubbleComponent,
  ],
  templateUrl: './send-test-sms-form.component.html',
  styleUrls: [
    '../../../../pages/organization/settings/settings-ui.css',
    './send-test-sms-form.component.css',
  ],
})
export class SendTestSmsFormComponent implements OnInit, OnChanges {
  private broadcastService = inject(BroadcastMessageService);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  // Each step replaces the last, so focus is moved into the new one by hand.
  @ViewChild('messageInput') private messageInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('confirmBody') private confirmBody?: ElementRef<HTMLElement>;
  @ViewChild('doneButton') private doneButton?: ElementRef<HTMLButtonElement>;

  @Input({ required: true }) openModal = false;
  @Input({ required: true }) contact: ITestContact | null = null;
  @Output() onClose = new EventEmitter<void>();

  step: Step = 'compose';
  /** Kept between opens, so the same copy can go to several phones. */
  message = '';
  senderId = '';
  senderIds: string[] = [];
  sendersLoading = false;
  sendersFailed = false;
  private sendersRequested = false;

  submitted = false;
  sending = false;
  sendError = '';
  result: SendResult | null = null;
  /** Text for the dialog's persistent live region. */
  announcement = '';

  get name(): string {
    const c = this.contact;
    return c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'this test number' : '';
  }

  get phone(): string {
    return displayPhone(this.contact?.primaryContactNumber) || '';
  }

  get partsLabel(): string {
    const parts = smsParts(this.message).parts;
    return parts === 1 ? '1 SMS part' : `${parts} SMS parts`;
  }

  get messageError(): string {
    return this.submitted && !this.message.trim() ? 'Write the message you want to test.' : '';
  }

  get senderError(): string {
    if (!this.submitted || this.senderId) return '';
    return this.senderIds.length ? 'Choose who the message comes from.' : '';
  }

  ngOnInit() {
    this.broadcastService.getSenderIDs$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((list: ISenderID[]) => this.applySenders(list));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['openModal']?.currentValue === true) {
      this.step = 'compose';
      this.submitted = false;
      this.sending = false;
      this.sendError = '';
      this.result = null;
      this.announcement = '';
      if (!this.sendersRequested || this.sendersFailed) this.loadSenders();
    }
  }

  private loadSenders() {
    this.sendersRequested = true;
    this.sendersLoading = true;
    this.sendersFailed = false;
    this.broadcastService
      .fetchSenderIDs()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list: ISenderID[]) => {
          this.sendersLoading = false;
          this.applySenders(list);
        },
        error: () => {
          this.sendersLoading = false;
          this.sendersFailed = true;
        },
      });
  }

  retrySenders() {
    this.loadSenders();
  }

  private applySenders(list: ISenderID[]) {
    const items = Array.isArray(list) ? list : [];
    // Active sender IDs first; the composer offers all of them, and so do we.
    const sorted = [...items].sort((a, b) => Number(b?.status === 1) - Number(a?.status === 1));
    this.senderIds = [...new Set(sorted.map((item) => (item?.senderId || '').toString()).filter(Boolean))];
    if (!this.senderId || !this.senderIds.includes(this.senderId)) {
      this.senderId = this.senderIds[0] || '';
    }
  }

  /** Compose → confirm. Nothing is sent yet. */
  review() {
    this.submitted = true;
    this.sendError = '';
    if (!this.contact?.primaryContactNumber) {
      this.sendError = 'This test number has no phone number. Edit it and add one first.';
      return;
    }
    if (!this.message.trim() || !this.senderId) return;
    this.step = 'confirm';
    this.focusAfterRender(() => this.confirmBody);
  }

  back() {
    this.step = 'compose';
    this.sendError = '';
    this.focusAfterRender(() => this.messageInput);
  }

  /** Focus an element of the step that is about to render, so it never falls to the page. */
  private focusAfterRender(target: () => ElementRef<HTMLElement> | undefined) {
    afterNextRender(() => target()?.nativeElement.focus(), { injector: this.injector });
  }

  send() {
    if (!this.contact?.id || this.sending) return;
    this.sending = true;
    this.sendError = '';

    this.broadcastService
      .sendTestMessage({
        message: this.message,
        senderId: this.senderId,
        testContactId: this.contact.id,
      })
      .subscribe({
        next: (response: any) => {
          this.sending = false;
          const delivery = (response?.deliveryStatus || response?.status || 'Sent').toString();
          this.result = {
            statusText: delivery,
            status: delivery,
            detail:
              response?.message ||
              'The gateway accepted the message. It usually reaches the phone within a minute or two.',
            reference: (response?.correlationId || '').toString(),
          };
          this.step = 'result';
          this.announcement = `Test result: ${this.result.statusText}. ${this.result.detail}`;
          this.focusAfterRender(() => this.doneButton);
        },
        error: (err) => {
          this.sending = false;
          const body = err?.error;
          this.sendError =
            body?.message ||
            body?.detail ||
            (err?.status === 0
              ? 'We could not reach the server. Check your connection and try again.'
              : 'The test was not sent. Check the sender ID and try again.');
        },
      });
  }

  /** Back to the message, to try a variation. */
  sendAnother() {
    this.step = 'compose';
    this.result = null;
    this.submitted = false;
    this.announcement = '';
    this.focusAfterRender(() => this.messageInput);
  }

  close() {
    if (this.sending) return;
    this.onClose.emit();
  }
}
